import { Component } from "@angular/core";
import { ColDef, GridReadyEvent } from "@ag-grid-community/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Entity } from "breeze-client";
import _ from "lodash";
import { ActiveStatusEnum, Product, ProductReplacement, ProductType } from "app/model/entities/entity-model";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { AgStyleFns } from "app/shared/ag-style-fns";
import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";
import { DomainService } from "app/shared/domain.service";

export interface ProductReplacementDialogParams extends DialogParams {
  oldProductTypeId: number;
  newProductTypeId: number;
}

/** One old product and the new product it maps to. */
interface MapRow {
  oldProduct: Product;
  newProductId: number | null;
  isConfirmed: boolean;
  existing?: ProductReplacement;
}

interface NewProductOption {
  id: number | null;
  label: string;
}

/**
 * Gatekeeper C2, the old-to-new product map. Each product of the old style (K500-OLD) maps to one
 * product of the new feed style (K500). The system proposes a new product with the same choices by
 * name (color, size and the rest), and a ProductAdmin confirms or corrects each line. When every live
 * old product is confirmed, the old style can go on Phaseout from here.
 *
 * Proximity (C3) and the pull pages (C4) use only confirmed lines.
 */
@Component({
  templateUrl: "./product-replacement-dialog.component.html",
  standalone: false,
})
export class ProductReplacementDialogComponent extends DialogBaseComponent<ProductReplacementDialogParams> {
  oldStyle: ProductType;
  newStyle: ProductType;
  rows: MapRow[] = [];
  newOptions: NewProductOption[] = [];
  gridOptions: GridOptionsExt;

  constructor(config: DynamicDialogConfig<ProductReplacementDialogParams>, dialogRef: DynamicDialogRef, domainService: DomainService) {
    super(config, dialogRef, domainService);
    this.prepare();
  }

  private async prepare() {
    this.oldStyle = await this.dbQueryService.getProductType(this.params.oldProductTypeId);
    this.newStyle = await this.dbQueryService.getProductType(this.params.newProductTypeId);
    const existing = await this.dbQueryService.getProductReplacementsForOldStyle(this.oldStyle.id);
    const existingByOld = new Map(existing.map((r) => [r.oldProductId, r]));

    const newProducts = _.sortBy(
      this.newStyle.products.filter((p) => p.activeStatusId !== ActiveStatusEnum.Inactive),
      (p) => p.featureChoicesExtract,
    );
    const labelCounts = _.countBy(newProducts, (p) => p.featureChoicesExtract);
    this.newOptions = [
      { id: null, label: "(no new product)" },
      ...newProducts.map((p) => ({
        id: p.id,
        label: labelCounts[p.featureChoicesExtract] > 1 ? `${p.featureChoicesExtract} (#${p.id})` : p.featureChoicesExtract,
      })),
    ];

    const proposals = this.proposeMap(this.oldStyle.products, newProducts);
    this.rows = _.sortBy(this.oldStyle.products, (p) => p.featureChoicesExtract).map((p) => {
      const e = existingByOld.get(p.id);
      return e
        ? { oldProduct: p, newProductId: e.newProductId, isConfirmed: e.confirmedTs != null, existing: e }
        : { oldProduct: p, newProductId: proposals.get(p.id) ?? null, isConfirmed: false };
    });

    Object.assign(this.config, { header: `Map Old Products - ${this.oldStyle.style} to ${this.newStyle.style}` });
    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady.bind(this),
      getRowId: (p) => String((p.data as MapRow).oldProduct.id),
      cellSelection: false,
    });
    this.isPageReady = true;
  }

  /**
   * The new product whose choices have the same names, feature kind by feature kind, as the old
   * product's. The old and new styles can use different features for the same question (two size
   * lists), so the match is on the choice names, not the choice ids. A product with no match, or
   * with more than one, is left for the ProductAdmin.
   */
  private proposeMap(oldProducts: Product[], newProducts: Product[]) {
    const byKey = _.groupBy(newProducts, (p) => this.choiceKey(p));
    const proposals = new Map<number, number>();
    oldProducts.forEach((p) => {
      const matches = byKey[this.choiceKey(p)] ?? [];
      if (matches.length === 1) proposals.set(p.id, matches[0].id);
    });
    return proposals;
  }

  private choiceKey(p: Product) {
    return _.sortBy(
      p.productFeatureChoices.map((pfc) => {
        const fc = pfc.featureChoice;
        const kind = fc?.feature?.featureKindId ?? `feature ${fc?.featureId}`;
        const name = (fc?.choiceValue ?? "").trim().toLowerCase().replace(/\s+/g, " ");
        return `${kind}=${name}`;
      }),
    ).join("|");
  }

  onGridReady(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      {
        headerName: "Old Product",
        colId: "old",
        valueGetter: (p) => (p.data as MapRow)?.oldProduct.featureChoicesExtract,
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(180, 2),
      },
      {
        headerName: "Status",
        valueGetter: (p) => ActiveStatusEnum[(p.data as MapRow)?.oldProduct.activeStatusId],
        filter: "agSetColumnFilter",
        ...AgFns.fixWidth(110),
      },
      {
        headerName: "New Product",
        colId: "new",
        ...AgFns.createDropdownEditor("newProductId", this.newOptions, (p) => this.onNewProductChanged(p.data as MapRow), "id", "label"),
        editable: true,
        cellStyle: AgStyleFns.setEditableStyle,
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(180, 2),
      },
      {
        headerName: "Map",
        colId: "map",
        valueGetter: (p) => this.mapState(p.data as MapRow),
        filter: "agSetColumnFilter",
        ...AgFns.fixWidth(120),
      },
      {
        ...AgFns.createCheckboxProps("Confirm", "isConfirmed", "confirm"),
        editable: (p) => (p.data as MapRow)?.newProductId != null,
        cellStyle: AgStyleFns.setEditableStyle,
        sortable: false,
        filter: false,
        onCellValueChanged: (p) => p.api.refreshCells({ rowNodes: [p.node], force: true }),
        ...AgFns.fixWidth(120),
      },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: "old", sort: "asc" }]);
  }

  private onNewProductChanged(row: MapRow) {
    // A different new product is a new answer, so it needs its own confirm.
    row.isConfirmed = false;
    this.gridOptions.apiX?.refreshCells({ force: true });
  }

  mapState(row: MapRow) {
    if (row == null) return "";
    if (row.newProductId == null) return "Not mapped";
    return row.isConfirmed ? "Confirmed" : "Proposed";
  }

  get mappedCount() {
    return this.rows.filter((r) => r.newProductId != null).length;
  }

  get confirmedCount() {
    return this.rows.filter((r) => r.newProductId != null && r.isConfirmed).length;
  }

  /** The old products that can still be ordered or used. Inactive ones need no map. */
  get liveRows() {
    return this.rows.filter((r) => r.oldProduct.activeStatusId !== ActiveStatusEnum.Inactive);
  }

  get isOldStyleLive() {
    return this.oldStyle.activeStatusId !== ActiveStatusEnum.Phaseout && this.oldStyle.activeStatusId !== ActiveStatusEnum.Inactive;
  }

  get oldStyleStatusName() {
    return this.oldStyle.activeStatusId === ActiveStatusEnum.Phaseout ? "on Phaseout" : "Inactive";
  }

  get canPhaseout() {
    return this.isOldStyleLive && this.liveRows.every((r) => r.newProductId != null && r.isConfirmed);
  }

  confirmAllMapped() {
    this.rows.filter((r) => r.newProductId != null).forEach((r) => (r.isConfirmed = true));
    this.gridOptions.apiX?.refreshCells({ force: true });
  }

  async save() {
    await this.saveMap(false);
  }

  async saveAndPhaseout() {
    if (!this.canPhaseout) return;
    const ok = await this.dialogService.yesNoDialog(
      "Put the Old Style on Phaseout?",
      `The system puts style ${this.oldStyle.style} on Phaseout.` +
        ` Proximity must first copy the setup of ${this.oldStyle.style} to ${this.newStyle.style} (Copy Setup in Proximity).` +
        `<br/><br/>Phaseout stops new orders for ${this.oldStyle.style}. The open orders on it finish.` +
        ` When all the open work is done, make the style Inactive on the Phaseout Drain page.` +
        `<br/><br/>Do you want to continue?`,
      "Ok",
      "Cancel",
    );
    if (!ok) return;
    await this.saveMap(true);
  }

  private async saveMap(phaseout: boolean) {
    const changed = this.buildChanges();
    // A changed map reaches Proximity only after this save, so Proximity cannot have copied it yet
    // and the server would refuse the Phaseout (gatekeeper C3c). Save the map alone, and say what is next.
    if (phaseout && changed.length > 0) {
      await this.wrapBusy(() => this.dbSaveService.saveOwnChanges(changed));
      await this.dialogService.infoDialog(
        "Copy the Setup in Proximity",
        `The system saved the map. Before ${this.oldStyle.style} can go on Phaseout, Proximity must copy its setup to ${this.newStyle.style}.` +
          `<br/><br/>In Proximity, open Product Types, find ${this.oldStyle.style}, and select Copy Setup.` +
          ` Then open this map again and select Save and Put ${this.oldStyle.style} on Phaseout.`,
      );
      this.dialogRef.close(true);
      return;
    }
    if (phaseout) {
      this.oldStyle.activeStatusId = ActiveStatusEnum.Phaseout;
      changed.push(this.oldStyle);
    }
    if (changed.length === 0) {
      this.dialogRef.close(false);
      return;
    }
    await this.wrapBusy(() => this.dbSaveService.saveOwnChanges(changed));
    this.toastr.success(
      phaseout
        ? `The system saved the map. Style ${this.oldStyle.style} is on Phaseout.`
        : `The system saved the map. ${this.confirmedCount} of ${this.rows.length} old products are confirmed.`,
    );
    this.dialogRef.close(true);
  }

  /** The server writes who proposed and who confirmed each line; the client sends the values it needs. */
  private buildChanges(): Entity[] {
    const initials = this.authUser.initials;
    const changed: Entity[] = [];
    this.rows.forEach((r) => {
      const e = r.existing;
      if (r.newProductId == null) {
        if (e) {
          e.entityAspect.setDeleted();
          changed.push(e);
        }
        return;
      }
      if (e == null) {
        changed.push(
          this.uow.createEntity(ProductReplacement, {
            oldProductId: r.oldProduct.id,
            newProductId: r.newProductId,
            crtnTs: new Date(),
            crtnUserInit: initials,
            modTs: new Date(),
            modUserInit: initials,
            confirmedTs: r.isConfirmed ? new Date() : null,
            confirmedUserInit: r.isConfirmed ? initials : null,
          }),
        );
        return;
      }
      const wasConfirmed = e.confirmedTs != null;
      if (e.newProductId !== r.newProductId) e.newProductId = r.newProductId;
      if (r.isConfirmed && (!wasConfirmed || e.entityAspect.entityState.isModified())) {
        e.confirmedTs = new Date();
        e.confirmedUserInit = initials;
      } else if (!r.isConfirmed && wasConfirmed) {
        e.confirmedTs = null;
        e.confirmedUserInit = null;
      }
      if (e.entityAspect.entityState.isModified()) changed.push(e);
    });
    return changed;
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
